/**
 * TUT-167 fatia 1 — Transportes: o espelho da lista fechada da API.
 *
 * Mora em `lib/` e não na pasta da tela porque **duas superfícies distintas
 * dependem dele**: a página de Viagens e o adapter de demonstração. Deixá-lo
 * dentro de `pages/frota/` obrigaria o adapter a importar de uma tela, que é a
 * dependência ao contrário — e a segunda cópia nasceria no dia seguinte.
 *
 * Aqui não há regra de negócio: a validação de coerência é do servidor (e do
 * `CHECK` de mão dupla do banco). O que este arquivo guarda é o VOCABULÁRIO —
 * a lista, os rótulos em português e as duas perguntas que a tela faz sobre uma
 * modalidade.
 */

import type {
  VehicleOwnership,
  VehicleTrip,
  VehicleTripMode,
  VehicleTripPayer,
} from "./types";

/**
 * TUT-167 fatia 1 — a modalidade é lista FECHADA.
 *
 * A ordem aqui é a ordem da tela e do filtro, e ela não é alfabética de
 * propósito: começa pelas duas que apontam carro do cadastro (frota e alugado
 * são as únicas com veículo), segue pelo carro do funcionário e termina no que
 * se contrata de terceiro. Quem lança do celular procura a sua nessa ordem.
 */
export const TRIP_MODES: readonly VehicleTripMode[] = [
  "FLEET",
  "RENTED",
  "OWN",
  "TAXI",
  "RIDE_HAILING",
  "AIR",
  "BUS",
];

export const tripModeLabel: Record<VehicleTripMode, string> = {
  FLEET: "Frota",
  RENTED: "Alugado",
  OWN: "Próprio",
  TAXI: "Táxi",
  RIDE_HAILING: "Aplicativo",
  AIR: "Aéreo",
  BUS: "Ônibus",
};

/** As duas que apontam um veículo do cadastro — espelho da regra do banco. */
export function tripModeUsesVehicle(mode: VehicleTripMode): boolean {
  return mode === "FLEET" || mode === "RENTED";
}

export const tripPayerLabel: Record<VehicleTripPayer, string> = {
  OFFICE: "Escritório absorve",
  REIMBURSE_PERSON: "Reembolsa a pessoa",
  BILL_CLIENT: "Cobra do cliente",
};

export const vehicleOwnershipLabel: Record<VehicleOwnership, string> = {
  OWNED: "Próprio",
  RENTED: "Alugado",
};

/**
 * O que a coluna "Transporte" do diário mostra.
 *
 * Frota e alugado mostram o carro (que sempre existe, pelo `CHECK` de mão
 * dupla); próprio mostra o carro do funcionário quando ele foi informado; o
 * resto mostra a própria modalidade. **Nunca devolve vazio** — célula em branco
 * numa linha de diário parece dado faltando, e aqui não falta nada: a viagem de
 * ônibus não tem carro por definição.
 */
export function tripTransportLabel(trip: {
  mode: VehicleTripMode;
  vehicleLabel: string | null;
  ownVehicleLabel: string | null;
}): string {
  if (tripModeUsesVehicle(trip.mode) && trip.vehicleLabel) {
    return trip.vehicleLabel;
  }
  if (trip.mode === "OWN" && trip.ownVehicleLabel) {
    return trip.ownVehicleLabel;
  }
  return tripModeLabel[trip.mode];
}

/**
 * O filtro por modalidade. `"TODAS"` é estado próprio, e não "nenhuma
 * selecionada": o diário nasce mostrando tudo, porque a razão de a modalidade
 * ser CAMPO — e não sete itens de menu — é justamente as sete viverem na mesma
 * lista.
 */
export function filterTripsByMode(
  trips: readonly VehicleTrip[],
  mode: VehicleTripMode | "TODAS",
): VehicleTrip[] {
  return mode === "TODAS"
    ? [...trips]
    : trips.filter((trip) => trip.mode === mode);
}

/**
 * Valor pago: NULO é ausência, ZERO é valor (a regra da TUT-129 fase 3).
 * `formatCurrency(0)` devolveria "R$ 0,00" para os dois casos, e é exatamente
 * essa confusão que a fatia veio evitar.
 */
export function formatTripAmount(
  cents: number | null,
  formatCurrency: (value: number) => string,
): string {
  return cents === null ? "não informado" : formatCurrency(cents / 100);
}

/**
 * As modalidades em que o odômetro significa alguma coisa — e, portanto, as
 * únicas em que a viagem pode ficar INCOMPLETA por falta de km. Espelho da
 * função de mesmo nome no domínio da API.
 *
 * Sem esta distinção, toda viagem de ônibus nasceria marcada "Incompleta" para
 * sempre, e alerta que nunca se resolve ensina todo mundo a ignorar o alerta.
 */
export function tripModeUsesOdometer(mode: VehicleTripMode): boolean {
  return mode === "FLEET" || mode === "RENTED" || mode === "OWN";
}
